export type * from './msal.types';

export type ThirdPartyCookieErrorCallback = (resource: string) => void;

export interface Authenticator {
  readonly type: AuthenticatorType;
  /**
   * Fetch a bearer token for the specified scopes.
   * @param scopes Array of scopes the application is requesting access to.
   * @param claims Optional claims to be included in the token.
   * @param tenantId Optional tenant ID, used to acquire token for account across tenants.
   * @param silent Otional flag indicating whether to attempt the token acquisition silently or allow a prompt to be shown.
   */
  acquireToken(scopes: string[], claims?: string | string[], tenantId?: string, silent?: boolean): Promise<string>;

  /**
   * Fetch a bearer token for a specified scopes by redirecting to login.microsoftonline.com.
   * @param scopes Array of scopes the application is requesting access to.
   */
  acquireTokenRedirect(scopes: string[], claims?: string | string[]): void;
  /**
   * Sets a callback on the authenticator to be called if a third party cookie error is encountered during token acquisition.
   * @param thirdPartyCookieErrorCallback The callback to be called when a third party cookie error is encountered.
   */
  setThirdPartyCookieErrorCallback(thirdPartyCookieErrorCallback: ThirdPartyCookieErrorCallback): void;
  logout?(): void;
}

export interface PopupAuthenticator extends Authenticator {
  /**
   * Initiating the login process via opening a popup window in the user's browser
   * @param scopes Array of scopes the application is requesting access to.
   */
  loginPopup(scopes: string[]): void;
  /**
   * Acquires token (interactive flow using a popup window) by sending request to AAD to obtain a new token.
   * @param scopes Array of scopes the application is requesting access to.
   */
  acquireTokenPopup(scopes: string[], claims?: string): Promise<void>;
}

export interface AuthenticatorOptions {
  /**
   * Azure Active Directory instance. Defaults to `https://login.microsoftonline.com/`.
   */
  readonly instance: string;
  /**
   * Your target tenant. Defaults to `common`.
   */
  readonly tenant: string;
  /**
   * Client ID assigned to your app by Azure Active Directory.
   */
  readonly clientId: string;
  /**
   * Where you want AAD to redirect the user after logout.
   */
  readonly postLogoutRedirectUri?: string;
  /**
   * Endpoint at which you expect to receive tokens.Defaults to `window.location.href`.
   */
  readonly redirectUri: string;
  /**
   * Query parameters to add to the authentication request.
   */
  readonly extraQueryParameters?: string;
  /**
   * Callback to be invoked when token acquisition fails.
   */
  onError?(error: unknown, properties: AuthenticationErrorProperties): Promise<void>;
}

export interface AuthenticationErrorProperties {
  readonly authenticatorType: AuthenticatorType;
  readonly scopes?: string[];
}

export enum AuthenticatorType {
  AdalPopup = 'AdalPopup',
  AdalRedirect = 'AdalRedirect',
  Custom = 'Custom',
  Electron = 'Electron',
  MsalPopup = 'MsalPopup',
  MsalRedirect = 'MsalRedirect',
  Outlook = 'Outlook',
  SharePoint = 'SharePoint',
  Teams = 'Teams',
  TeamsMeeting = 'TeamsMeeting',
  TeamsMeetingV2 = 'TeamsMeetingV2',
  VivaConnections = 'VivaConnections',
  YammerJwt = 'YammerJwt',
  NestedAppAuth = 'NestedAppAuth',
}
